import { compareNames } from '@obraplan/core/domain/shared/naming';
import {
  consolidationKey,
  type ConsolidatedItem,
} from '@obraplan/core/domain/takeoff/consolidation';
import {
  isHourUnit,
  LABOR_SOURCE_LABEL,
  LABOR_WARNING_LABEL,
  type PricedLabor,
} from '@obraplan/core/domain/takeoff/labor-rate';
import {
  ORIGIN_LABEL,
  WARNING_LABEL,
  type PricedMaterial,
} from '@obraplan/core/domain/takeoff/material-price';
import {
  groupBudget,
  priceBudgetLine,
  summarizeBudget,
  type BudgetDiscipline,
  type BudgetLine,
  type BudgetLineInput,
  type BudgetPriceOrigin,
  type BudgetSummary,
} from '@obraplan/core/domain/takeoff/project-budget';
import type { ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import { isPurchasable } from '@obraplan/core/domain/takeoff/takeoff-line';
import { TOM_DA_FONTE } from './custo-da-mao-de-obra';
import { ROTULO_CURTO_DA_ORIGEM, TOM_DA_ORIGEM } from './precos-da-obra';

/**
 * O orçamento da obra, montado da consolidação e dos preços (Fase 29, etapa 4 — D15, D16).
 *
 * A Consolidação vira o avesso do quantitativo — um material por linha, com os serviços que o
 * consomem abaixo —, e o preço é de **material**, não de uso (D7). O orçamento é o lado de lá:
 * disciplina → serviço → item, com o total de cada nível. Cada **uso** vira uma linha, com o
 * preço do material e a quantidade daquele serviço.
 *
 * Quem conta é o domínio (`project-budget.ts`): esta parte só põe as linhas na ordem da obra e
 * as entrega a ele. A mesma linha serve ao orçamento vivo, montado aqui da tela, e ao aprovado,
 * que o servidor guarda com as linhas já prontas.
 */

/**
 * De onde veio o preço, por extenso — o `title` do selo e a coluna "Origem" da planilha.
 *
 * Os do material (RN-398) e os da função (RN-399) num só mapa: `PROVISORIO` é o mesmo nos dois.
 */
export const ROTULO_DA_ORIGEM: Readonly<Record<BudgetPriceOrigin, string>> = {
  MEDIO: ORIGIN_LABEL.MEDIO,
  PROVISORIO: ORIGIN_LABEL.PROVISORIO,
  DIGITADO: ORIGIN_LABEL.DIGITADO,
  OBRA: LABOR_SOURCE_LABEL.OBRA,
  SISTEMA: LABOR_SOURCE_LABEL.SISTEMA,
};

/** O rótulo do selo ao lado do preço — o de `ROTULO_DA_ORIGEM`, sem o "do compras". */
export const ROTULO_CURTO_DA_ORIGEM_NO_ORCAMENTO: Readonly<Record<BudgetPriceOrigin, string>> = {
  ...ROTULO_CURTO_DA_ORIGEM,
  OBRA: LABOR_SOURCE_LABEL.OBRA,
  SISTEMA: LABOR_SOURCE_LABEL.SISTEMA,
};

/** O tom do selo: o normal é neutro, o emprestado informa, o provisório pede atenção. */
export const TOM_DA_ORIGEM_NO_ORCAMENTO: Readonly<
  Record<BudgetPriceOrigin, 'neutral' | 'warn' | 'info'>
> = { ...TOM_DA_ORIGEM, ...TOM_DA_FONTE };

/** Onde o serviço está no catálogo: a disciplina, o serviço e cada item dentro dele. */
interface PosicaoNoCatalogo {
  readonly disciplina: number;
  readonly servico: number;
  readonly itens: ReadonlyMap<string, number>;
}

/**
 * A posição de cada serviço no catálogo, pela chave dele.
 *
 * A ordem do catálogo é a da obra: disciplina na ordem de execução, serviço na ordem em que o
 * cadastro os guarda, item na ordem do serviço. É a ordem em que alguém lê um orçamento — e não
 * a alfabética, que é a da consolidação e serve a quem procura um material.
 */
function posicoesDoCatalogo(
  catalogo: Pick<ServiceCatalog, 'disciplines'>,
): ReadonlyMap<string, PosicaoNoCatalogo> {
  const posicoes = new Map<string, PosicaoNoCatalogo>();
  catalogo.disciplines.forEach((disciplina, indiceDaDisciplina) => {
    disciplina.services.forEach((servico, indiceDoServico) => {
      const itens = new Map<string, number>();
      servico.items.forEach((item, indiceDoItem) => {
        const chave = consolidationKey(item);
        if (!itens.has(chave)) itens.set(chave, indiceDoItem);
      });
      posicoes.set(servico.key, {
        disciplina: indiceDaDisciplina,
        servico: indiceDoServico,
        itens,
      });
    });
  });
  return posicoes;
}

/** O que não está no catálogo vai para o fim, e não some: orçamento com linha escondida mente. */
const FORA_DO_CATALOGO = Number.MAX_SAFE_INTEGER;

/** O preço de um item como a linha o guarda: o número, de onde veio, e a ressalva em texto. */
interface PrecoDoItem {
  readonly unitPrice?: number;
  readonly origin?: BudgetPriceOrigin;
  readonly warning?: string;
}

/**
 * O preço do material como o `priceMaterials` o resolveu.
 *
 * O aviso é a frase do domínio, palavra por palavra (`WARNING_LABEL`): a tela e a planilha
 * dizem o mesmo que a Consolidação diz. O material provisório também pode trazer aviso — é o
 * "sem compra no período" que explica por que o preço não é o médio.
 */
function precoDoMaterial({ resolved }: PricedMaterial<ConsolidatedItem>): PrecoDoItem {
  return {
    ...(resolved.unitPrice === undefined || resolved.origin === undefined
      ? {}
      : { unitPrice: resolved.unitPrice, origin: resolved.origin }),
    ...(resolved.warning === undefined ? {} : { warning: WARNING_LABEL[resolved.warning] }),
  };
}

/** O R$/h da função como o `priceLabor` o resolveu, com a frase do domínio no aviso. */
function precoDaMaoDeObra({ rate }: PricedLabor<ConsolidatedItem>): PrecoDoItem {
  return {
    ...(rate.perHour === undefined || rate.source === undefined
      ? {}
      : { unitPrice: rate.perHour, origin: rate.source }),
    ...(rate.warning === undefined ? {} : { warning: LABOR_WARNING_LABEL[rate.warning] }),
  };
}

/** Uma linha por **uso** do item: o serviço, a parte dele na quantidade e o preço do item. */
function linhasDoItem(
  item: ConsolidatedItem,
  preco: PrecoDoItem,
  unidade: string | undefined,
): readonly BudgetLineInput[] {
  return item.usages.map((uso): BudgetLineInput => ({
    disciplineName: uso.disciplineName,
    serviceKey: uso.serviceKey,
    serviceName: uso.serviceName,
    kind: item.kind,
    itemKey: item.key,
    ...(item.code === undefined ? {} : { code: item.code }),
    description: item.description,
    ...(unidade === undefined || unidade.trim() === '' ? {} : { unit: unidade }),
    quantity: uso.quantity,
    // Preço e origem andam juntos (o contrato do aprovado recusa um sem o outro).
    ...(preco.unitPrice === undefined || preco.origin === undefined
      ? {}
      : { unitPrice: preco.unitPrice, priceOrigin: preco.origin }),
    ...(preco.warning === undefined ? {} : { warning: preco.warning }),
  }));
}

/**
 * As linhas do orçamento, na ordem da obra (D15).
 *
 * `materiais` e `maoDeObra` são os itens da **obra inteira** já com o preço que vale
 * (`priceMaterials`, `priceLabor`); `catalogo` só dá a ordem. Disciplina na ordem do catálogo,
 * serviço na ordem dele, e dentro do serviço o material primeiro e a mão de obra depois — cada
 * um na ordem do item no serviço. O que o catálogo não conhece vai para o fim, por nome.
 *
 * A mão de obra sai em homem-hora (RN-166): a unidade da linha é `HH`, e o preço, o R$/h.
 */
export function linhasDoOrcamento(
  materiais: readonly PricedMaterial<ConsolidatedItem>[],
  maoDeObra: readonly PricedLabor<ConsolidatedItem>[],
  catalogo: Pick<ServiceCatalog, 'disciplines'>,
): readonly BudgetLineInput[] {
  const daMaterial = materiais.flatMap((linha) =>
    linhasDoItem(linha.item, precoDoMaterial(linha), linha.item.unit),
  );
  const daMaoDeObra = maoDeObra.flatMap((linha) =>
    linhasDoItem(
      linha.item,
      precoDaMaoDeObra(linha),
      // A hora é "HH"; a unidade que não é hora aparece como é, que é o que o aviso acusa.
      isHourUnit(linha.item.unit) ? 'HH' : linha.item.unit,
    ),
  );

  const posicoes = posicoesDoCatalogo(catalogo);
  const chaveDeOrdem = (linha: BudgetLineInput) => {
    const posicao = posicoes.get(linha.serviceKey);
    return {
      disciplina: posicao?.disciplina ?? FORA_DO_CATALOGO,
      servico: posicao?.servico ?? FORA_DO_CATALOGO,
      classe: isPurchasable(linha.kind) ? 0 : 1,
      item: posicao?.itens.get(linha.itemKey) ?? FORA_DO_CATALOGO,
    };
  };

  return [...daMaterial, ...daMaoDeObra]
    .map((linha) => ({ linha, ordem: chaveDeOrdem(linha) }))
    .sort(
      (a, b) =>
        a.ordem.disciplina - b.ordem.disciplina ||
        a.ordem.servico - b.ordem.servico ||
        // Fora do catálogo, a posição não diz nada: agrupa pelo nome, para a disciplina e o
        // serviço não se espalharem (o `groupBudget` os junta pela ordem em que aparecem).
        compareNames(a.linha.disciplineName, b.linha.disciplineName) ||
        compareNames(a.linha.serviceName, b.linha.serviceName) ||
        a.ordem.classe - b.ordem.classe ||
        a.ordem.item - b.ordem.item ||
        compareNames(a.linha.description, b.linha.description),
    )
    .map(({ linha }) => linha);
}

/** O orçamento pronto para a tela: as linhas com o total, a árvore e a soma. */
export interface OrcamentoMontado {
  readonly linhas: readonly BudgetLine[];
  readonly disciplinas: readonly BudgetDiscipline[];
  readonly resumo: BudgetSummary;
}

/**
 * Põe o total em cada linha, agrupa e soma — com as funções do domínio, as mesmas que o
 * servidor usa para refazer a conta do aprovado.
 */
export function orcamentoDasLinhas(linhas: readonly BudgetLineInput[]): OrcamentoMontado {
  const comTotal = linhas.map(priceBudgetLine);
  return {
    linhas: comTotal,
    disciplinas: groupBudget(comTotal),
    resumo: summarizeBudget(comTotal),
  };
}

/** O orçamento vivo da obra: as linhas na ordem da obra, já com total, grupos e soma. */
export function orcamentoDaObra(
  materiais: readonly PricedMaterial<ConsolidatedItem>[],
  maoDeObra: readonly PricedLabor<ConsolidatedItem>[],
  catalogo: Pick<ServiceCatalog, 'disciplines'>,
): OrcamentoMontado {
  return orcamentoDasLinhas(linhasDoOrcamento(materiais, maoDeObra, catalogo));
}

/**
 * O catálogo com as disciplinas na ordem de execução da obra.
 *
 * O catálogo guarda a ordem em que as disciplinas chegaram, que não quer dizer nada para quem
 * lê; o cadastro da estrutura guarda a de execução (pilar antes de alvenaria), e é ela que o
 * recorte de cada tipologia já segue. O orçamento da obra inteira lê o catálogo inteiro, e por
 * isso a aplica aqui — senão ele abriria em outra ordem que a das listas ao lado.
 *
 * `ordem` é a posição de cada disciplina pela chave (`EscopoDaObra.ordemDaDisciplina`); a que
 * não está nela vai para o fim, na ordem em que o catálogo a tinha.
 */
export function naOrdemDaObra(
  catalogo: ServiceCatalog,
  ordem: ReadonlyMap<string, number>,
): ServiceCatalog {
  const disciplines = catalogo.disciplines
    .map((disciplina, indice) => ({ disciplina, indice }))
    .sort(
      (a, b) =>
        (ordem.get(a.disciplina.key) ?? FORA_DO_CATALOGO) -
          (ordem.get(b.disciplina.key) ?? FORA_DO_CATALOGO) || a.indice - b.indice,
    )
    .map(({ disciplina }) => disciplina);
  return { ...catalogo, disciplines };
}
